import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, message, Upload, Avatar } from 'antd';
import { UserOutlined, UploadOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { updateUser } from '../../../services/user/user.service';
import { uploadAvatar } from '../../../services/user/avatar.service';
import { updateUserProfile, logout } from '@stores/reducers/auth.reducer';

export default function EditProfileModal({ visible, onClose }) {
  const [form] = Form.useForm();
  const user = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (visible && user) {      
      form.setFieldsValue({
        name: user.name,
        email: user.email,
        password: '',
        confirmPassword: '',
      });
      setShowPasswordFields(false);
      setAvatarUrl(user.url_photo || null);
    }
  }, [visible, user, form]);

  const handleUpload = async ({ file }) => {
    
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
    if (!isJpgOrPng) {
      message.error('Você só pode fazer upload de arquivos JPG/PNG!');
      return;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('A imagem deve ser menor que 2MB!');
      return;
    }

    setUploading(true);
    try {
    
      const userId = user?.id;
      if (!userId) {
        throw new Error('ID do usuário não encontrado');
      } 
      
      const result = await uploadAvatar(file, userId);
      setAvatarUrl(result.url);      
      
      dispatch(updateUserProfile({ url_photo: result.url }));
      
      message.success('Avatar carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload do avatar:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Erro ao carregar avatar!';
      message.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      const updateData = {
        name: values.name,
        email: values.email,
        url_photo: avatarUrl || undefined,
      };

      if (showPasswordFields && values.password) {
        updateData.password = values.password;
      }

      await updateUser(user.id, updateData);      
      
      if (showPasswordFields && values.password) {
        message.success('Perfil atualizado com sucesso! Faça login novamente com a nova senha.');
        onClose();
        dispatch(logout());
      } else {        
        dispatch(updateUserProfile({
          name: values.name,
          email: values.email,
          url_photo: avatarUrl
        }));
        message.success('Perfil atualizado com sucesso!');
        onClose();
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.message || e?.message || 'Erro ao atualizar perfil!';
      message.error(errorMsg);
    }
  };

  return (
    <Modal
      title="Perfil"
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item
          label="Avatar"
          style={{ textAlign: 'center', marginBottom: 24 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{ position: 'relative' }}>
              <Avatar
                size={80}
                src={avatarUrl}
                icon={!avatarUrl && <UserOutlined />}
                style={{
                  border: '2px solid #f0f0f0',
                  cursor: 'pointer'
                }}
              />
              {uploading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  borderRadius: '50%',
                  color: 'white',
                  fontSize: '12px'
                }}>
                  Enviando...
                </div>
              )}
            </div>
            <Upload
              customRequest={handleUpload}
              showUploadList={false}
              accept="image/jpeg,image/jpg,image/png"
              disabled={uploading}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                size="small"
                type="dashed"
              >
                {uploading ? 'Enviando...' : 'Alterar Avatar'}
              </Button>
            </Upload>
            <div style={{ fontSize: '12px', color: '#999', textAlign: 'center' }}>
              Formatos aceitos: JPG, PNG<br />
              Tamanho máximo: 2MB
            </div>
          </div>
        </Form.Item>

        <Form.Item label="Nome" name="name">
          <Input disabled readOnly />
        </Form.Item>

        <Form.Item label="E-mail" name="email">
          <Input disabled readOnly />
        </Form.Item>

        {!showPasswordFields && (
          <Form.Item>
            <Button type="default" block onClick={() => setShowPasswordFields(true)}>
              Alterar senha
            </Button>
          </Form.Item>
        )}

        {showPasswordFields && (
          <>
            <Form.Item
              label="Senha"
              name="password"
              rules={showPasswordFields ? [{ required: true, message: 'Informe a nova senha!' }] : []}
              extra="Preencha apenas se deseja alterar a senha."
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              label="Confirmar Senha"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Confirme a nova senha!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!form.getFieldValue('password') && !value) {
                      return Promise.resolve();
                    }
                    if (form.getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('As senhas não batem!'));
                  },
                }),
              ]}
              extra="Digite novamente a nova senha."
            >
              <Input.Password />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block style={{ marginBottom: 8 }}>
                Salvar
              </Button>
              <Button
                type="default"
                block
                onClick={() => {
                  setShowPasswordFields(false);
                  form.setFieldsValue({ password: '', confirmPassword: '' });
                }}
              >
                Cancelar
              </Button>
            </Form.Item>
          </>
        )}

        {!showPasswordFields && (
          <Form.Item>
            <Button type="default" block onClick={onClose}>
              Fechar
            </Button>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}